import React, { FunctionComponent } from 'react';

import { useEvent } from 'effector-react/ssr';

import { setIsOpenFeedback } from '@stores/dialogs';
import { useCommonData } from '@stores/global-data';

import { formatPlusSevenPhone, formatRussianPhoneNumber } from '@utils/contacts';
import { openCookieSettings } from '@utils/cookie-consent';
import { CONFETTI } from '@utils/env';
import { makeHomeRoute } from '@utils/route';

import { Button } from '@components/common/Button/Button';
import { Container } from '@components/common/Container/Container';
import { FooterTelegram } from '@components/common/FooterTelegram/FooterTelegram';
import { Link } from '@components/common/Link/Link';
import { FeipLogoIcon, SiteLogoIcon } from '@components/icons';

import styles from './Footer.module.scss';

export const Footer: FunctionComponent = () => {
    const {
        settings: {
            footer_block: { text },
            telegram_block,
        },
        contacts: {
            footer: { phones },
        },
        menu: { footer: menu },
    } = useCommonData();

    const currentYear = new Date().getFullYear();

    const setIsOpenFeedbackFn = useEvent(setIsOpenFeedback);
    const openFeedbackModal = () => setIsOpenFeedbackFn(true);

    const hasSomeTelegramField = [...Object.values(telegram_block)].some(Boolean);

    return (
        <div className={styles.root}>
            <Container classes={{ root: styles.container }}>
                <div className={styles.inner}>
                    {hasSomeTelegramField && <FooterTelegram />}
                    <div className={styles.middleRow}>
                        <div className={styles.columnAbout}>
                            <p className={styles.caption}>О компании</p>
                            <div className={styles.links}>
                                {menu.map((item, index) =>
                                    item.footer_type === 'about_company' ? (
                                        <Link key={index} route={item.url} className={styles.link}>
                                            {item.name}
                                        </Link>
                                    ) : null,
                                )}
                            </div>
                        </div>
                        <div className={styles.columnInfo}>
                            <p className={styles.caption}>Покупателям</p>
                            <div className={styles.links}>
                                {menu.map((item, index) =>
                                    item.footer_type === 'customers' ? (
                                        <Link key={index} route={item.url} className={styles.link}>
                                            {item.name}
                                        </Link>
                                    ) : null,
                                )}
                            </div>
                        </div>
                        <div className={styles.columnContacts}>
                            <p className={styles.caption}>Колл-центр</p>
                            <div className={styles.phones}>
                                {phones.map((item, index) => (
                                    <Link
                                        key={index}
                                        route={`tel:${formatPlusSevenPhone(item)}`}
                                        className={styles.phoneLink}
                                    >
                                        {formatRussianPhoneNumber(item)}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className={styles.columnLogo}>
                            <div className={styles.siteLogo}>
                                {CONFETTI && (
                                    <img
                                        src="/images/confetti/confetti-logo.svg"
                                        className={styles.logoConfetti}
                                        alt=""
                                        loading="lazy"
                                    />
                                )}
                                <Link route={makeHomeRoute()} className={styles.siteLogoLink}>
                                    <SiteLogoIcon className={styles.siteLogo} />
                                </Link>
                            </div>
                            <p className={styles.logoCaption}>{text}</p>
                            <Button variant="white" isFullWith onClick={openFeedbackModal}>
                                Задать вопрос
                            </Button>
                        </div>
                    </div>
                    <div className={styles.bottomRow}>
                        <div className={styles.copyrights}>© {currentYear} ГурманМ</div>
                        <div className={styles.bottomLinks}>
                            <button
                                type="button"
                                className={styles.cookieSettings}
                                onClick={openCookieSettings}
                                data-testid="cookie-settings-link"
                            >
                                Настройки cookie
                            </button>
                            <Link route="https://feip.co/" target="blank">
                                <FeipLogoIcon className={styles.feipLogo} />
                            </Link>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};
